import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

// Apple-style image that slides/scales gently as it scrolls through the viewport.
// On mobile the movement is reduced for a calmer, clearer feel.
const ParallaxImage = ({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  direction = "up", // up | left | right
  intensity = 70,
  style,
  ...rest
}) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const amt = isMobile ? intensity * 0.4 : intensity;
  const axisRange =
    direction === "left"
      ? [-amt, 0, -amt]
      : direction === "right"
      ? [amt, 0, amt]
      : [amt, 0, -amt];

  const move = useTransform(scrollYProgress, [0, 0.5, 1], axisRange);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1, 1.04]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [0.7, 1, 1, 0.85]);

  const hasPos = /\b(absolute|fixed|sticky|relative)\b/.test(className);
  const posCls = hasPos ? "" : "relative";

  if (reduce) {
    return (
      <div className={`${posCls} overflow-hidden ${className}`} style={style} {...rest}>
        <img src={src} alt={alt} className={`w-full h-full object-cover ${imgClassName}`} />
      </div>
    );
  }

  const motionStyle =
    direction === "left" || direction === "right"
      ? { x: move, scale, opacity }
      : { y: move, scale, opacity };

  return (
    <div ref={ref} className={`${posCls} overflow-hidden ${className}`} style={style} {...rest}>
      <motion.img
        src={src}
        alt={alt}
        style={motionStyle}
        className={`w-full h-full object-cover will-change-transform ${imgClassName}`}
      />
    </div>
  );
};

export default ParallaxImage;
